import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { logout } from "../../store/authSlice";
import { accumulateDevicePages } from "../../utils/devicePagination";

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_BACKEND_URL || "http://localhost:7600/api",
  prepareHeaders: (headers, { getState }) => {
    const token = getState().auth?.token || localStorage.getItem("token");
    if (token) headers.set("x-auth-token", token);
    return headers;
  },
});

const baseQueryWithReauth = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);

  if (result.error) {
    const status = result.error.status;

    // 🔐 ONLY token expired
    if (status === 401) {
      api.dispatch(logout());
    }

    // 🚫 Permission issue → NO logout
    if (status === 403) {
      console.warn(
        "Permission denied:",
        result.error.data?.msg || "Not allowed"
      );
    }
  }

  return result;
};

export const dashboardApi = createApi({
  reducerPath: "dashboardApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["Users", "Devices"],
  endpoints: (builder) => ({
    getDashboardSummary: builder.query({
      async queryFn(arg, _api, _extra, fetchWithBQ) {
        // Summary cache entries must be tied to the authenticated account.
        // The scope is intentionally only a client cache key; authorization
        // remains entirely server-side through the JWT.
        const refresh = arg?.refresh === true;
        const primary = await fetchWithBQ({
          url: "/devices/summary",
          ...(refresh ? { params: { refresh: "true" } } : {}),
        });
        if (!primary.error && primary.data) {
          return {
            data: {
              counts: primary.data?.counts || {},
              totalUsers: Number(primary.data?.totalUsers || 0),
              generatedAt: primary.data?.generatedAt || null,
              total: Number(primary.data?.total || primary.data?.counts?.total || 0),
              source: "devices-summary",
            },
          };
        }

        const fallback = await fetchWithBQ({ url: "/dashboard/summary" });
        if (!fallback.error) {
          return {
            data: {
              counts: fallback.data?.counts || {},
              totalUsers: Number(fallback.data?.totalUsers || 0),
              generatedAt: fallback.data?.generatedAt || null,
              total: Number(fallback.data?.counts?.total || 0),
              source: "dashboard-summary",
            },
          };
        }

        return { error: primary.error || fallback.error };
      },
    }),

    // ✅ Fetch All Users (From adminRoutes)
    getAllUsers: builder.query({
      query: () => ({ url: "/admin/users" }), // Matches router.get('/users') in adminRoutes mounted at /api/admin
      providesTags: ["Users"],
      transformResponse: (response) => {
        // Backend might return { users: [...] } or just [...]
        return response.users || response.data || response;
      }
    }),

    // ✅ Fetch All Devices via paginated loop — avoids slow single "return all" path
    // and works reliably for large fleets (1000+ devices).
    getAllDevices: builder.query({
      async queryFn(arg, _api, _extra, fetchWithBQ) {
        const PAGE_LIMIT = 500; // server caps at 500; fewer round-trips for large fleets
        // lite=true by default: Devices-list views don't need live GPS/odometer.
        const { lite = true, ...rest } = (arg && typeof arg === "object") ? arg : {};
        const baseParams = { ...rest };
        if (lite) baseParams.lite = "true";
        const extra = Object.entries(baseParams)
          .filter(([, v]) => v !== undefined && v !== null && v !== "")
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
          .join("&");
        try {
          const all = await accumulateDevicePages({
            pageLimit: PAGE_LIMIT,
            concurrency: 4,
            allowPartialOnLaterError: true,
            fetchPage: async (page) => {
              const qs = `page=${page}&limit=${PAGE_LIMIT}${extra ? `&${extra}` : ""}`;
              const result = await fetchWithBQ({ url: `/devices?${qs}` });
              if (result.error) throw result.error;
              return result.data;
            },
          });
          return { data: all };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["Devices"],
    }),
    
  }),
});

export const { useGetDashboardSummaryQuery, useGetAllUsersQuery, useGetAllDevicesQuery } = dashboardApi;
